/**
 * ValidationService
 * Validates discovery answers against product/business context
 */
export class ValidationService {
    /**
     * Validate answer against product/business context
     * @param {string} questionKey
     * @param {string} answer
     * @param {Object} context - { product, business }
     * @returns {Array<{type, message}>} warnings
     */
    validate(questionKey, answer, context = {}) {
        const warnings = [];
        const { product, business } = context;

        switch (questionKey) {
            case 'offer':
                warnings.push(...this._validateOffer(answer, product, business));
                break;
            case 'businessModel':
                warnings.push(...this._validateBusinessModel(answer, business));
                break;
            case 'buyer':
                warnings.push(...this._validateBuyer(answer, product));
                break;
            case 'constraints':
                warnings.push(...this._validateConstraints(answer, business));
                break;
        }

        return warnings;
    }

    _validateOffer(answer, product, business) {
        const warnings = [];
        const lowerAnswer = answer.toLowerCase();

        // Check pricing mentions
        const priceMatch = lowerAnswer.match(/\$(\d+(?:,\d{3})*(?:\.\d{2})?)/);
        if (priceMatch && business?.pricing) {
            const userPricing = parseFloat(priceMatch[1].replace(/,/g, ''));
            const knownOfferValue = product?.offer?.value || '';
            const knownPriceMatch = knownOfferValue.match(/\$(\d+(?:,\d{3})*(?:\.\d{2})?)/);

            if (knownPriceMatch) {
                const knownPricing = parseFloat(knownPriceMatch[1].replace(/,/g, ''));
                if (Math.abs(userPricing - knownPricing) > knownPricing * 0.2) {
                    warnings.push({
                        type: 'offer-mismatch',
                        message: `You said $${userPricing}, but I found ${knownOfferValue} in the docs. Which offer should we use?`
                    });
                }
            }
        }

        return warnings;
    }

    _validateBusinessModel(answer, business) {
        const warnings = [];
        const lowerAnswer = answer.toLowerCase();

        if (!business?.pricing) return warnings;

        if (lowerAnswer.includes('subscription') && business.pricing.model === 'usage-based') {
            warnings.push({
                type: 'model-conflict',
                message: `You said subscription, but docs show usage-based pricing. Should we adjust?`
            });
        }

        if (lowerAnswer.includes('one-time') && business.pricing.model === 'subscription') {
            warnings.push({
                type: 'model-conflict',
                message: `You said one-time, but docs show subscription model. Which is correct?`
            });
        }

        return warnings;
    }

    _validateBuyer(answer, product) {
        const warnings = [];
        if (!product?.targetAudience) return warnings;

        const lowerAnswer = answer.toLowerCase();
        const knownAudience = product.targetAudience.toLowerCase();

        if (lowerAnswer.includes('individual') && knownAudience.includes('enterprise')) {
            warnings.push({
                type: 'audience-mismatch',
                message: `You mentioned individuals, but docs suggest enterprise teams. Should we clarify?`
            });
        }

        if (lowerAnswer.includes('enterprise') && knownAudience.includes('individual')) {
            warnings.push({
                type: 'audience-mismatch',
                message: `You mentioned enterprise, but docs suggest individuals. Which is the primary buyer?`
            });
        }

        return warnings;
    }

    _validateConstraints(answer, business) {
        const warnings = [];
        if (!business?.positioning) return warnings;

        const lowerAnswer = answer.toLowerCase();
        const positioning = business.positioning.toLowerCase();

        if (lowerAnswer.includes('self-host') && positioning.includes('managed')) {
            warnings.push({
                type: 'positioning-conflict',
                message: `You mentioned self-hosting, but positioning says managed service. Should we clarify the constraint?`
            });
        }

        return warnings;
    }
}
